"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Globe, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface StrategyItem {
  id: number;
  title: string;
  description: string;
  badge: string;
  iconName: "TrendingUp" | "Globe" | "ShieldCheck" | "ArrowUpRight";
}
const strategies: StrategyItem[] = [{
  id: 1,
  title: "Dynamic Pricing & Intraday Yielding",
  description: "Real-time rate positioning based on demand pace, local events, and lead-time pickup to capture peak average daily rate.",
  badge: "Yield Optimization",
  iconName: "TrendingUp"
}, {
  id: 2,
  title: "OTA Distribution & Algorithm Optimization",
  description: "Full listing audit and visibility ranking on Booking.com, MakeMyTrip, Agoda, and Expedia to maximize organic conversion.",
  badge: "Channel Reach",
  iconName: "Globe"
}, {
  id: 3,
  title: "Rate Parity Defense",
  description: "Eradicating rogue wholesaler rates and undercutting leaks across metasearch engines and Google Hotel Ads.",
  badge: "Margin Protection",
  iconName: "ShieldCheck"
}, {
  id: 4,
  title: "Direct Booking Conversion",
  description: "Connecting PMS and channel managers directly to Google Free Booking Links and custom high-converting booking engines.",
  badge: "Direct Acquisition",
  iconName: "ArrowUpRight"
}];
export default function RevenueGrid() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: StrategyItem["iconName"]) => {
    switch (iconName) {
      case "TrendingUp":
        return <TrendingUp className="h-6 w-6 text-secondary" />;
      case "Globe":
        return <Globe className="h-6 w-6 text-secondary" />;
      case "ShieldCheck":
        return <ShieldCheck className="h-6 w-6 text-secondary" />;
      case "ArrowUpRight":
        return <ArrowUpRight className="h-6 w-6 text-secondary" />;
      default:
        return <TrendingUp className="h-6 w-6 text-secondary" />;
    }
  };
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="revenuestrategygrid" data-nav="light" className="relative bg-background py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-secondary/40 bg-secondary/10 px-3.5 py-1 text-xs font-semibold tracking-wider text-foreground uppercase">
            Core Revenue Methodology
          </Badge>
          <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Institutional-Grade Revenue Strategy Tailored to Your Property
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Structured hotel revenue architecture built to capture market demand,
            secure price integrity, and scale net operating profits.
          </p>
        </div>

        {/* 4-Item Strategy Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {strategies.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className="h-full">
              <Card className="group relative flex h-full flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-secondary hover:shadow-md">
                <div>
                  <div className="mb-6 flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-muted/40 transition-[background-color] duration-300 group-hover:bg-secondary/10">
                      {getIcon(item.iconName)}
                    </div>
                    <span className="font-mono text-xs font-semibold text-muted-foreground">
                      0{item.id}
                    </span>
                  </div>

                  <CardHeader className="p-0">
                    <Badge variant="outline" className="mb-2.5 w-fit border-border bg-muted/30 text-[11px] font-medium text-foreground">
                      {item.badge}
                    </Badge>
                    <CardTitle className="font-serif text-xl font-bold leading-snug text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="mt-3 p-0">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </CardContent>
                </div>

                <div className="mt-6 border-t border-border pt-4">
                  <span className="inline-flex items-center text-xs font-semibold text-foreground transition-colors group-hover:text-secondary">
                    Learn strategy details
                    <ArrowUpRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA Banner */}
        <div className="mt-14 flex flex-col items-center justify-between gap-6 rounded-xl border border-border bg-muted/30 p-6 sm:flex-row sm:px-8 sm:py-6">
          <div className="text-center sm:text-left">
            <h3 className="font-serif text-lg font-bold text-foreground">
              Ready to audit your property&apos;s revenue potential?
            </h3>
            <p className="text-sm text-muted-foreground">
              Get an in-depth review of your OTA distribution, rate parity, and yield levers.
            </p>
          </div>
          <Button asChild size="lg" className="w-full sm:w-auto rounded-lg">
            <Link to="#">
              Request a Revenue Audit
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}